// Localização: frontend/src/pages/Pacientes/PlanoMedicoPacientePage.js (versão 0.2.3)

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useLocation } from 'react-router-dom'; 
import { get, post, put, remove } from '../../api/api'; 
import PageTitle from '../../components/Layout/PageTitle';
import PlanoMedicoList from '../../components/PlanoMedico/PlanoMedicoList';
import PlanoMedicoForm from '../../components/PlanoMedico/PlanoMedicoForm';
import PrimaryButton from '../../components/Common/PrimaryButton';
import Modal from '../../components/UI/Modal';
import LoadingIndicator from '../../components/Common/LoadingIndicator';
import GoBackButton from '../../components/Common/GoBackButton'; 
import { useMedicamentos } from '../../hooks/useMedicamentos'; 
// 💡 NOVO: Importa o hook de Lotes
import { useLotes } from '../../hooks/useLotes'; 
import { usePacientes } from '../../hooks/usePacientes';
import { usePermissions } from '../../context/PermissionContext';
import { useNotification } from '../../context/NotificationContext';
import { Pill, Plus, UserCircle, Calendar, AlertTriangle } from 'lucide-react';
import { getPacienteFotoUrl } from '../../utils/imageUtils';

const PlanoMedicoPacientePage = () => {
    const { pacienteId } = useParams();
    const location = useLocation(); 
    const { pacienteNome: nomePassado } = location.state || {}; 
    
    // 1. CONSUMINDO OS HOOKS
    const { medicamentos, isLoadingMedicamentos, fetchMedicamentos } = useMedicamentos();
    const { lotes, isLoadingLotes, fetchLotes } = useLotes();
    const { fetchPacienteById } = usePacientes();
    
    const { checkPermission } = usePermissions();
    const { success: showSuccess, error: showError } = useNotification();

    const [planos, setPlanos] = useState([]);
    const [isLoadingPlanos, setIsLoadingPlanos] = useState(false);
    const [error, setError] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingPlano, setEditingPlano] = useState(null);
    const [pacienteNome, setPacienteNome] = useState(nomePassado || `ID ${pacienteId}`);
    const [pacienteCompleto, setPacienteCompleto] = useState(null); 
    
    const canCreate = checkPermission('planos_medicos', 'can_create');
    const canEdit = checkPermission('planos_medicos', 'can_edit');
    const canDelete = checkPermission('planos_medicos', 'can_delete');
    const canCancel = checkPermission('planos_medicos', 'can_edit'); // Cancelar usa a mesma permissão de editar

    // Funções de busca
    const fetchPlanos = useCallback(async () => {
        // ... (lógica inalterada)
        setIsLoadingPlanos(true);
        setError(null);
        try {
            const response = await get(`/planos-medicos/paciente/${pacienteId}`); 
            setPlanos(response.data.data || response.data || []);
        } catch (error) {
            console.error("Erro ao buscar planos do paciente:", error);
            setError('Falha ao carregar planos de medicação do paciente.');
        } finally {
            setIsLoadingPlanos(false);
        }
    }, [pacienteId]);

    // 💡 Efeito para buscar dados completos do paciente
    useEffect(() => {
        const fetchPacienteData = async () => {
            try {
                const pacienteData = await fetchPacienteById(parseInt(pacienteId));
                setPacienteCompleto(pacienteData);
                if (pacienteData?.nome_completo) {
                    setPacienteNome(pacienteData.nome_completo);
                }
            } catch (error) {
                console.error("Erro ao buscar dados do paciente:", error);
                // Mantém o nome passado via state se houver
            }
        };
        
        if (pacienteId) {
            fetchPacienteData();
        }
    }, [pacienteId, fetchPacienteById]);

    // Efeito para carregar planos e dados globais (Medicamentos/Lotes)
    useEffect(() => {
        fetchPlanos();
        // 🎯 OBRIGA A CARREGAR OS DADOS DO HOOK
        fetchMedicamentos(); 
        fetchLotes(); 
    }, [fetchPlanos, fetchMedicamentos, fetchLotes]); // Adicionadas as dependências de fetch

    const handleSavePlano = async (formData) => {
        // ... (lógica inalterada)
        try {
            const dataToSend = { ...formData, paciente_id: parseInt(pacienteId) }; 

            if (editingPlano) {
                await put(`/planos-medicos/${editingPlano.id}`, dataToSend);
                showSuccess('Plano atualizado com sucesso!');
            } else {
                await post('/planos-medicos', dataToSend);
                showSuccess('Plano de Medicação criado com sucesso!');
            }
            fetchPlanos();
            setIsModalOpen(false);
        } catch (error) {
            const msg = error.response?.data?.message || error.message || 'Erro de conexão.';
            showError(`Erro ao salvar plano: ${msg}`);
            throw new Error(`Erro: ${msg}`);
        }
    };
    
    const handleCancelPlano = async (plano) => {
        if (!window.confirm(`Deseja cancelar o plano de medicação "${plano.nome_medicamento}"? Isso cancelará todas as tarefas futuras pendentes relacionadas a este plano.`)) return;
        
        try {
            // Define data_fim como hoje para cancelar o plano
            const hoje = new Date().toISOString().split('T')[0];
            await put(`/planos-medicos/${plano.id}`, {
                data_fim: hoje
            });
            showSuccess('Plano cancelado com sucesso! Tarefas futuras foram canceladas.');
            fetchPlanos();
        } catch (error) {
            const msg = error.response?.data?.message || error.message || 'Erro de conexão.';
            showError(`Falha ao cancelar o plano: ${msg}`);
        }
    };
    
    const handleDeletePlano = async (planoId) => {
        // ... (lógica inalterada)
        if (!window.confirm("Tem certeza? Isso excluirá o plano permanentemente e cancelará tarefas futuras pendentes.")) return;
        try {
            await remove(`/planos-medicos/${planoId}`);
            showSuccess('Plano excluído com sucesso!');
            fetchPlanos();
        } catch (error) {
            const msg = error.response?.data?.message || error.message || 'Erro de conexão.';
            showError(`Falha ao excluir o plano: ${msg}`);
        }
    };

    // 2. ATUALIZADO: Indicador de loading combinado
    const overallLoading = isLoadingPlanos || isLoadingMedicamentos || isLoadingLotes;

    // 💡 Cálculo de estatísticas para o card informativo
    const planosAtivos = planos.filter(plano => {
        const hoje = new Date();
        hoje.setHours(0, 0, 0, 0);
        const dataFim = plano.data_fim ? new Date(plano.data_fim) : null;
        return !dataFim || dataFim >= hoje;
    }).length;

    // 💡 Lotes próximos de vencimento (próximos 90 dias) - filtrados por paciente
    const lotesProximosVencimento = React.useMemo(() => {
        if (!lotes || lotes.length === 0) return [];
        const hoje = new Date();
        hoje.setHours(0, 0, 0, 0);
        const noventaDias = new Date();
        noventaDias.setDate(hoje.getDate() + 90);
        noventaDias.setHours(23, 59, 59, 999);
        
        return lotes.filter(lote => {
            // Filtra lotes alocados ao paciente ou de uso geral (paciente_id null)
            // Para esta visualização, mostraremos apenas lotes alocados ao paciente
            const pertenceAoPaciente = lote.paciente_id === parseInt(pacienteId);
            
            if (!lote.data_validade || !pertenceAoPaciente) return false;
            const dataValidade = new Date(lote.data_validade);
            dataValidade.setHours(0, 0, 0, 0);
            return dataValidade >= hoje && dataValidade <= noventaDias && lote.quantidade_atual > 0;
        })
        .sort((a, b) => new Date(a.data_validade) - new Date(b.data_validade))
        .slice(0, 3); // Limita a 3 lotes mais próximos de vencer
    }, [lotes, pacienteId]);

    return (
        <div className="p-4 sm:p-6 lg:p-8">
            <div className="max-w-7xl mx-auto">
                <GoBackButton />
                
                <PageTitle 
                    title="Planos de Medicação"
                    subtitle={`Gerenciamento de Tratamentos para o Paciente: ${pacienteNome}`}
                >
                    {canCreate && (
                        <PrimaryButton 
                            onClick={() => { setEditingPlano(null); setIsModalOpen(true); }}
                            className="bg-teal-600 hover:bg-teal-700"
                        >
                            <Plus className="w-5 h-5 mr-2" /> Novo Plano
                        </PrimaryButton>
                    )}
                </PageTitle>

            {error && <div className="text-red-500 mb-4 p-2 bg-red-50 rounded border border-red-300">{error}</div>}

            {/* 💡 Card Informativo do Paciente */}
            {pacienteCompleto && !overallLoading && (
                <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 mb-6 border border-gray-200">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                        {/* Foto e Nome do Paciente */}
                        <div className="flex items-center gap-4 flex-1">
                            {pacienteCompleto.fotoURL ? (
                                <img
                                    src={getPacienteFotoUrl(pacienteCompleto.fotoURL)}
                                    alt={pacienteCompleto.nome_completo}
                                    className="h-16 w-16 sm:h-20 sm:w-20 rounded-full object-cover border-2 border-teal-200 flex-shrink-0"
                                    onError={(e) => {
                                        e.target.style.display = 'none';
                                        const iconElement = e.target.parentElement.querySelector('.fallback-icon');
                                        if (iconElement) iconElement.style.display = 'block';
                                    }}
                                />
                            ) : null}
                            <UserCircle 
                                size={80} 
                                className="text-gray-300 fallback-icon" 
                                style={{ display: pacienteCompleto.fotoURL ? 'none' : 'block' }} 
                            />
                            <div>
                                <h3 className="text-lg sm:text-xl font-bold text-gray-900">
                                    {pacienteCompleto.nome_completo || pacienteNome}
                                </h3>
                                <p className="text-sm text-gray-500 mt-1">Informações do Paciente</p>
                            </div>
                        </div>

                        {/* Estatísticas */}
                        <div className="flex flex-wrap gap-4 sm:gap-6 w-full sm:w-auto">
                            {/* Planos Ativos */}
                            <div className="flex items-center gap-2 bg-teal-50 px-4 py-2 rounded-lg border border-teal-200">
                                <Pill className="text-teal-600" size={20} />
                                <div>
                                    <p className="text-xs text-gray-600">Planos Ativos</p>
                                    <p className="text-lg font-bold text-teal-700">{planosAtivos}</p>
                                </div>
                            </div>

                            {/* Lotes Próximos de Vencimento */}
                            <div className="flex items-center gap-2 bg-orange-50 px-4 py-2 rounded-lg border border-orange-200">
                                <AlertTriangle className="text-orange-600" size={20} />
                                <div>
                                    <p className="text-xs text-gray-600">Lotes Próximos</p>
                                    <p className="text-lg font-bold text-orange-700">
                                        {lotesProximosVencimento.length}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Lista de Lotes Próximos de Vencimento */}
                    {lotesProximosVencimento.length > 0 && (
                        <div className="mt-4 pt-4 border-t border-gray-200">
                            <p className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                                <Calendar className="text-orange-600" size={16} />
                                Lotes Próximos de Vencimento (próximos 90 dias)
                            </p>
                            <div className="flex flex-wrap gap-2">
                                {lotesProximosVencimento.map((lote) => {
                                    const dataValidade = new Date(lote.data_validade);
                                    const hoje = new Date();
                                    hoje.setHours(0, 0, 0, 0);
                                    const diasParaVencer = Math.ceil((dataValidade - hoje) / (1000 * 60 * 60 * 24));
                                    
                                    return (
                                        <div 
                                            key={lote.id || `${lote.medicamento_id}-${lote.lote}`}
                                            className="bg-orange-50 border border-orange-200 rounded-md px-3 py-1.5 text-xs"
                                        >
                                            <span className="font-medium text-orange-800">
                                                {lote.nome_medicamento || 'Medicamento'} - Lote {lote.lote}
                                            </span>
                                            <span className="text-orange-600 ml-2">
                                                {diasParaVencer <= 0 
                                                    ? `(Vencido há ${Math.abs(diasParaVencer)} dias)`
                                                    : `(Vence em ${diasParaVencer} dias)`
                                                }
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {overallLoading ? (
                <LoadingIndicator />
            ) : (
                <PlanoMedicoList 
                    planos={planos}
                    onEdit={(plano) => { setEditingPlano(plano); setIsModalOpen(true); }}
                    onDelete={handleDeletePlano}
                    onCancel={handleCancelPlano}
                    canEdit={canEdit}
                    canDelete={canDelete}
                    canCancel={canCancel}
                    hideSearch={true}
                />
            )}
            
            {/* Modal de Cadastro/Edição */}
            <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={editingPlano ? 'Editar Plano' : 'Cadastrar Novo Plano Médico'}>
                <PlanoMedicoForm 
                    initialData={editingPlano || { paciente_id: parseInt(pacienteId) }}
                    onSubmit={handleSavePlano}
                    onClose={() => setIsModalOpen(false)}
                    isEditing={!!editingPlano}
                    medicamentosList={medicamentos}
                    // 💡 Passa os dados completos do paciente (incluindo fotoURL e status)
                    pacientesList={pacienteCompleto ? [{
                        id: pacienteCompleto.id,
                        nome_completo: pacienteCompleto.nome_completo || pacienteNome,
                        fotoURL: pacienteCompleto.fotoURL,
                        status: pacienteCompleto.status || 'Ativo'
                    }] : [{ id: parseInt(pacienteId), nome_completo: pacienteNome, status: 'Ativo' }]} 
                    // 🎯 NOVO: Passando a lista de lotes
                    lotesList={lotes} 
                />
            </Modal>
            </div>
        </div>
    );
};

export default PlanoMedicoPacientePage;